import React, { useState, useEffect, useRef } from "react";
import {
  TextField,
  Checkbox,
  ListItemText,
  MenuItem,
  Select,
  OutlinedInput,
  InputLabel,
  FormControl,
} from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { ThemeProvider } from "@emotion/react";
import { colors } from "../../common/pallette/colors";
import { AvatarAndName } from "../AvatarAndName";
import { baseUrl } from "../../api/config";

const theme = createTheme({
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderColor: "black !important", // Always keep black border
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "black !important", // Override MUI focus border
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "black !important", // Keep black on hover
          },
        },
        notchedOutline: {
          borderColor: "black !important", // Ensures the outline stays black
        },
        input: {
          padding: '10px'
        }
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: "black !important", // Label stays black
          "&.Mui-focused": {
            color: "black !important", // Ensure label stays black when focused
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          maxHeight: '200px !important'
        }
      }
    },
  },
});

export default function AvatarMultiSelectCheckBox(props: any) {
  const { label, options, selectedValues, setSelectedValues, manageOnFocus } =
    props;
  const selectRef = useRef<HTMLDivElement | null>(null);
  const [searchText, setSearchText] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);

  useEffect(() => {
    if (selectRef.current) {
      selectRef.current.focus();
    }
  }, [selectRef]);

  useEffect(() => {
    setFilteredOptions(
      options.filter((option: any) =>
        option.displayName.toLowerCase().includes(searchText.toLowerCase())
      )
    );
  }, [searchText, options]);

  const handleChange = (selectedObj: any) => {
    setSelectedValues(selectedObj);
  };

  return (
    <ThemeProvider theme={theme}>
      <FormControl fullWidth variant="outlined" focused>
        <InputLabel>{label}</InputLabel>
        <Select
          multiple
          fullWidth
          autoFocus
          value={selectedValues || []}
          inputRef={selectRef}
          className={'always-focused'}
          input={<OutlinedInput label={label} />}
          sx={{
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "black !important", // Ensure the default border is black
              },
              "&:hover fieldset": {
                borderColor: "black !important", // Keep black on hover
              },
              "&.Mui-focused fieldset": {
                borderColor: "black !important", // Ensure black even on focus
              },
            },
          }}
          renderValue={(selected) =>
            selected.map((opt: any) => opt?.displayName).join(", ")
          }
          MenuProps={{
            disablePortal: true,
            disableAutoFocusItem: true,
            PaperProps: {
              onMouseDown: (e: any) => e.stopPropagation(),
            },
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            manageOnFocus(e);
          }}
          onClick={(e) => {
            e.stopPropagation();
            manageOnFocus(e);
          }}
        >
          <MenuItem disableRipple>
            <TextField
              fullWidth
              placeholder="Search..."
              variant="outlined"
              size="small"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </MenuItem>
          {filteredOptions.map((option: any) => (
            <MenuItem
              key={option.id}
              value={option}
              onClick={(e) => {
                handleChange(option);
                e.stopPropagation();
                manageOnFocus(e);
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                manageOnFocus(e);
              }}
            >
              <Checkbox
                sx={{ transform: "scale(0.8)", padding: "0px" }}
                checked={selectedValues?.some(
                  (item: any) => item.id === option.id
                )}
              />
              <AvatarAndName
                avatarName={option.displayName || ""}
                avatarImage={`${baseUrl}/Content${option.avatarPath}`}
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </ThemeProvider>
  );
}
